/**
 * PIX API Service
 * Frontend service for individual PIX payment operations
 */

import { cleanDocument, formatCnpjString } from "@/utils";

// ============================================================================
// ERROR HANDLING
// ============================================================================

export class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

export interface APIResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface PIXFormData {
  destinationAccount: string;
  amount: string;
  paymentDate: string;
  costCenter?: string;
  description: string;
  selectedFavorito?: any;
}

export interface PIXPayment {
  id: string;
  uploadId: string;
  recipientName: string;
  pixKey: string;
  pixKeyType: string;
  amount: number;
  paymentDate: string;
  paymentStatus:
    | "pending"
    | "approved"
    | "rejected"
    | "paid"
    | "cancelled"
    | "requested"
    | "processing"
    | "completed"
    | "returned"
    | "failed"
    | "expired"
    | "refunded";
  information?: string;
  approvedBy?: string;
  approvalDate?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  rejectionDate?: string;
  submissionStatus?: string;
  externalPaymentId?: string;
  createdAt: string;
  upload?: {
    id: string;
    fileName: string;
    processingDate: string;
  };
}

export interface PIXPaymentDetails extends PIXPayment {
  beneficiaryDocument?: string;
  fullAddress?: string;
  phone?: string;
  email?: string;
  submissionMessage?: string;
  paymentConfirmedAt?: string;
  paymentConfirmedAmount?: number;
  updatedAt: string;
}

export interface PIXSubmissionResponse {
  uploadId: string;
  paymentId: string;
  amount: number;
  recipient: string;
  pixKey: string;
  status: string;
  createdAt: string;
}

export interface PIXStats {
  totalUploads: number;
  totalPayments: number;
  totalAmount: number;
  paymentsByStatus: {
    pending: { count: number; amount: number };
    approved: { count: number; amount: number };
    rejected: { count: number; amount: number };
    paid: { count: number; amount: number };
    cancelled: { count: number; amount: number };
  };
}

export interface PIXWebhookTestResult {
  success: boolean;
  status: number;
  message: string;
  responseTime: number;
}

// ============================================================================
// API BASE CONFIGURATION
// ============================================================================

const API_BASE_URL =
  import.meta.env.VITE_N8N_PIX_API_URL || "http://localhost:3000/api";
const PIX_API_URL = `${API_BASE_URL}/pix`;

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<APIResponse<T>> {
  try {
    const url = endpoint.startsWith("http")
      ? endpoint
      : `${PIX_API_URL}${endpoint}`;

    console.log(`[PIX_API_SERVICE] Making request to: ${url}`);
    // Append Authorization header when token exists
    let authHeaders: Record<string, string> = {};
    try {
      const token = localStorage.getItem("barte-auth-token");
      if (token) {
        authHeaders = { Authorization: `Bearer ${token}` };
      }
    } catch {
      // ignore storage errors
    }

    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    });

    const data = await response.json();

    if (!response.ok) {
      console.error(`[PIX_API_SERVICE] API Error (${response.status}):`, data);
      throw new APIError(
        data.message || `HTTP ${response.status}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    console.error("[PIX_API_SERVICE] Request failed:", error);

    if (error instanceof APIError) {
      throw error;
    }

    throw new APIError(
      error instanceof Error ? error.message : "Network error",
      0,
      error
    );
  }
}

// ============================================================================
// PIX PAYMENT OPERATIONS
// ============================================================================

/**
 * Submit individual PIX payment
 */
export async function submitPIXPayment(
  formData: PIXFormData
): Promise<APIResponse<PIXSubmissionResponse>> {
  const keyDigits = cleanDocument(formData?.destinationAccount || "");
  const isDocument = keyDigits.length === 11 || keyDigits.length === 14;
  const sanitizedFormData: PIXFormData = {
    ...formData,
    destinationAccount: isDocument ? keyDigits : formData.destinationAccount,
  };

  return apiRequest<PIXSubmissionResponse>("/submit", {
    method: "POST",
    body: JSON.stringify(sanitizedFormData),
  });
}

/**
 * Approve PIX payment
 */
export async function approvePIXPayment(
  paymentId: string,
  approvedBy: string
): Promise<APIResponse<any>> {
  console.log(`[PIX_API_SERVICE] Approving PIX payment: ${paymentId}`);

  return apiRequest<any>(`/approve/${paymentId}`, {
    method: "POST",
    body: JSON.stringify({ approvedBy }),
  });
}

/**
 * Reject PIX payment
 */
export async function rejectPIXPayment(
  paymentId: string,
  rejectedBy: string,
  reason: string
): Promise<APIResponse<any>> {
  console.log(`[PIX_API_SERVICE] Rejecting PIX payment: ${paymentId}`);

  return apiRequest<any>(`/reject/${paymentId}`, {
    method: "POST",
    body: JSON.stringify({ rejectedBy, reason }),
  });
}

// ============================================================================
// PIX PAYMENT QUERIES
// ============================================================================

/**
 * Get list of PIX payments
 */
export async function getPIXPayments(
  status?: string,
  limit: number = 50
): Promise<APIResponse<PIXPayment[]>> {
  console.log("[PIX_API_SERVICE] Fetching PIX payments");

  const params = new URLSearchParams();
  if (status) params.append("status", status);
  params.append("limit", limit.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/payments?${queryString}` : "/payments";

  return apiRequest<PIXPayment[]>(endpoint);
}

/**
 * Get PIX payment details
 */
export async function getPIXPaymentDetails(
  paymentId: string
): Promise<APIResponse<PIXPaymentDetails>> {
  console.log(`[PIX_API_SERVICE] Fetching PIX payment details: ${paymentId}`);

  return apiRequest<PIXPaymentDetails>(`/payments/${paymentId}`);
}

/**
 * Get PIX payments statistics
 */
export async function getPIXStats(): Promise<APIResponse<PIXStats>> {
  console.log("[PIX_API_SERVICE] Fetching PIX statistics");

  return apiRequest<PIXStats>("/stats");
}

// ============================================================================
// PIX UTILITIES
// ============================================================================

/**
 * Test PIX webhook connection
 */
export async function testPIXWebhookConnection(): Promise<
  APIResponse<PIXWebhookTestResult>
> {
  console.log("[PIX_API_SERVICE] Testing PIX webhook connection");

  return apiRequest<PIXWebhookTestResult>("/test-webhook", {
    method: "POST",
  });
}

export { validatePIXKey } from "@/utils";

// formatCurrency movido para src/lib/utils.ts para evitar duplicação

/**
 * Format PIX key for display
 */
export function formatPIXKey(pixKey: string, keyType: string): string {
  switch (keyType) {
    case "CPF":
      return pixKey.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    case "CNPJ":
      return formatCnpjString(pixKey);
    case "PHONE":
      if (pixKey.startsWith("+55")) {
        const numbers = pixKey.slice(3);
        return numbers.replace(/(\d{2})(\d{5})(\d{4})/, "+55 ($1) $2-$3");
      }
      return pixKey;
    case "EMAIL":
    case "RANDOM_KEY":
    default:
      return pixKey;
  }
}

/**
 * Get PIX key type label for display
 */
export function getPIXKeyTypeLabel(keyType: string): string {
  const labels = {
    CPF: "CPF",
    CNPJ: "CNPJ",
    EMAIL: "E-mail",
    PHONE: "Telefone",
    RANDOM_KEY: "Chave Aleatória",
  };
  return labels[keyType as keyof typeof labels] || keyType;
}

/**
 * Get payment status label for display
 */
export function getPaymentStatusLabel(status: string): string {
  const normalized = String(status || "").toLowerCase();
  const labels: Record<string, string> = {
    requested: "Solicitado",
    pending: "Pendente",
    pending_approval: "Agendado",
    scheduled: "Agendado",
    approved: "Aprovado",
    rejected: "Rejeitado",
    processing: "Processando",
    paid: "Pago",
    completed: "Pago",
    returned: "Devolvido",
    cancelled: "Cancelado",
    failed: "Falhou",
    expired: "Expirado",
    refunded: "Reembolsado",
  };
  return labels[normalized] || status;
}

/**
 * Get payment status color for UI
 */
export function getPaymentStatusColor(status: string): string {
  const normalized = String(status || "").toLowerCase();
  const colors: Record<string, string> = {
    requested: "hsla(36, 38%, 74%, 0.25)",
    pending: "hsla(36, 38%, 74%, 0.25)",
    pending_approval: "hsla(36, 38%, 74%, 0.25)",
    scheduled: "hsla(36, 38%, 74%, 0.25)",
    approved: "hsla(223, 80.60%, 26.30%, 0.10)",
    rejected: "hsla(342, 65%, 45%, 0.10)",
    processing: "hsla(278, 67.10%, 31.00%, 0.10)",
    paid: "hsla(223, 67%, 31%, 0.10)",
    completed: "hsla(223, 80.60%, 26.30%, 0.10)",
    returned: "hsla(36, 38%, 74%, 0.25)",
    cancelled: "hsla(0, 0%, 0%, 0.08)",
    failed: "hsla(342, 17%, 72%, 0.25)",
    expired: "hsla(0, 0%, 0%, 0.08)",
    refunded: "hsla(36, 38%, 74%, 0.25)",
  };
  return colors[normalized] || "#6B7280";
}

// Centraliza o tema de cores por status (badge background, texto e ícone)
export interface PaymentStatusColors {
  badgeBg: string;
  text: string;
  icon: string;
}

export function getPaymentStatusColors(status: string): PaymentStatusColors {
  const normalized = String(status || "").toLowerCase();

  // Cor de texto padrão já utilizada no componente
  const defaultText = "hsla(223, 59%, 29%, 1)";

  const map: Record<string, PaymentStatusColors> = {
    requested: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: defaultText,
      icon: defaultText,
    },
    // pendente (não especificado) → mantém padrão no texto
    pending: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: defaultText,
      icon: defaultText,
    },
    // agendado
    pending_approval: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    scheduled: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    // pago
    approved: {
      badgeBg: "hsla(223, 80.60%, 26.30%, 0.10)",
      text: "hsla(223, 59%, 29%, 1)",
      icon: "hsla(223, 59%, 29%, 1)",
    },
    paid: {
      badgeBg: "hsla(223, 67%, 31%, 0.10)",
      text: "hsla(223, 59%, 29%, 1)",
      icon: "hsla(223, 59%, 29%, 1)",
    },
    completed: {
      badgeBg: "hsla(223, 80.60%, 26.30%, 0.10)",
      text: "hsla(223, 59%, 29%, 1)",
      icon: "hsla(223, 59%, 29%, 1)",
    },
    // processamento (não especificado) → mantém padrão
    processing: {
      badgeBg: "hsla(278, 67.10%, 31.00%, 0.10)",
      text: defaultText,
      icon: defaultText,
    },
    // erro
    rejected: {
      badgeBg: "hsla(342, 65%, 45%, 0.10)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    failed: {
      badgeBg: "hsla(342, 17%, 72%, 0.25)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    expired: {
      badgeBg: "hsla(0, 0%, 0%, 0.08)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    // cancelada e desistência
    cancelled: {
      badgeBg: "hsla(0, 0%, 0%, 0.08)",
      text: "hsla(0, 0%, 25%, 1)",
      icon: "hsla(0, 0%, 25%, 1)",
    },
    canceled: {
      badgeBg: "hsla(0, 0%, 0%, 0.08)",
      text: "hsla(0, 0%, 25%, 1)",
      icon: "hsla(0, 0%, 25%, 1)",
    },
    withdrawn: {
      badgeBg: "hsla(0, 0%, 0%, 0.08)",
      text: "hsla(0, 0%, 25%, 1)",
      icon: "hsla(0, 0%, 25%, 1)",
    },
    desistencia: {
      badgeBg: "hsla(0, 0%, 0%, 0.08)",
      text: "hsla(0, 0%, 25%, 1)",
      icon: "hsla(0, 0%, 25%, 1)",
    },
    // estornada e contestada
    returned: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(36, 64%, 29%, 1)",
      icon: "hsla(36, 64%, 29%, 1)",
    },
    refunded: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(36, 64%, 29%, 1)",
      icon: "hsla(36, 64%, 29%, 1)",
    },
    chargeback: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(36, 64%, 29%, 1)",
      icon: "hsla(36, 64%, 29%, 1)",
    },
    disputed: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(36, 64%, 29%, 1)",
      icon: "hsla(36, 64%, 29%, 1)",
    },
  };

  const colors = map[normalized];
  if (colors) return colors;

  return {
    badgeBg: "hsla(223, 80.60%, 26.30%, 0.10)",
    text: defaultText,
    icon: defaultText,
  };
}

// ============================================================================
// REACT HOOKS FOR PIX OPERATIONS
// ============================================================================

/**
 * Hook for PIX payment submission
 */
export function useSubmitPIXPayment() {
  const submitPayment = async (formData: PIXFormData) => {
    try {
      const response = await submitPIXPayment(formData);
      return response;
    } catch (error) {
      console.error("[PIX_HOOK] Error submitting payment:", error);
      throw error;
    }
  };

  return { submitPayment };
}

/**
 * Hook for PIX payments list
 */
export function usePIXPayments(status?: string, limit: number = 50) {
  const fetchPayments = async () => {
    try {
      const response = await getPIXPayments(status, limit);
      return response.data || [];
    } catch (error) {
      console.error("[PIX_HOOK] Error fetching payments:", error);
      throw error;
    }
  };

  return { fetchPayments };
}

/**
 * Hook for PIX payment approval/rejection
 */
export function usePIXPaymentActions() {
  const approvePayment = async (paymentId: string, approvedBy: string) => {
    try {
      const response = await approvePIXPayment(paymentId, approvedBy);
      return response;
    } catch (error) {
      console.error("[PIX_HOOK] Error approving payment:", error);
      throw error;
    }
  };

  const rejectPayment = async (
    paymentId: string,
    rejectedBy: string,
    reason: string
  ) => {
    try {
      const response = await rejectPIXPayment(paymentId, rejectedBy, reason);
      return response;
    } catch (error) {
      console.error("[PIX_HOOK] Error rejecting payment:", error);
      throw error;
    }
  };

  return { approvePayment, rejectPayment };
}
